/**
 * PaymentSettingsCard — Phase 2 controls for Stripe Connect payments.
 *
 * Lives in Settings → Payments, BELOW the StripeConnectCard. Only useful
 * after the user has connected their Stripe account. Lets the workspace
 * owner / admin toggle:
 *
 *   • ACH bank transfers (cheaper than cards on big invoices)
 *   • Card-fee absorb (client pays the 2.9% + $0.30 instead of the subscriber)
 *   • Platform fee — 'off' | 'percent' | 'flat' so the CountBucks operator
 *     can take a small cut of every payment (revenue).
 *
 * All toggles persist to workspace.payment_settings via
 * GET/PUT /api/stripe-connect/payment-settings.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Banknote, Percent, DollarSign, Settings as SettingsIcon } from 'lucide-react';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { apiFetch } from '../../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SETTINGS = {
  accept_ach: true,
  absorb_card_fee: false,
  platform_fee_mode: 'percent',
  platform_fee_percent: 0.6,
  platform_fee_flat_cents: 0,
};

export default function PaymentSettingsCard({ currentWorkspace }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connected, setConnected] = useState(false);
  const wsId = currentWorkspace?.workspace_id;

  useEffect(() => {
    if (!wsId) return;
    (async () => {
      try {
        // Check Stripe Connect status first — only show settings if connected
        const sr = await apiFetch(`${API_URL}/api/stripe-connect/status?workspace_id=${wsId}`);
        if (sr.ok) {
          const sdata = await sr.json();
          setConnected(!!(sdata.connected && sdata.charges_enabled));
        }
        const r = await apiFetch(`${API_URL}/api/stripe-connect/payment-settings?workspace_id=${wsId}`);
        if (r.ok) {
          const data = await r.json();
          setSettings({ ...DEFAULT_SETTINGS, ...data });
        }
      } catch (e) {
        console.error('Failed to load payment settings', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [wsId]);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await apiFetch(`${API_URL}/api/stripe-connect/payment-settings?workspace_id=${wsId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Save failed');
      setSettings(next);
      toast.success('Payment settings saved');
    } catch (e) {
      toast.error(e.message || 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  const update = (patch) => save({ ...settings, ...patch });

  if (!connected) {
    return null; // Only show after the user has connected their Stripe account
  }
  if (loading) {
    return (
      <div className="rounded-xl border border-zinc-200 dark:border-white/10 p-6 bg-card flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading payment settings…
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-zinc-200 dark:border-white/10 p-6 bg-card space-y-6"
      data-testid="payment-settings-card"
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <SettingsIcon className="w-4 h-4 text-emerald-500" />
            Payment Behavior
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            How clients pay your invoices and who absorbs the processing cost.
          </p>
        </div>
        {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </div>

      {/* ACH toggle */}
      <div className="flex items-start justify-between gap-4 py-3 border-t border-zinc-100 dark:border-white/5">
        <div className="flex-1">
          <div className="font-medium text-sm flex items-center gap-2">
            <Banknote className="w-4 h-4 text-blue-500" />
            Accept ACH bank transfers
            <Badge variant="outline" className="text-[10px]">0.8% capped at $5</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1 max-w-md">
            Adds "Pay by bank account" alongside cards at checkout. Much cheaper than 2.9% cards
            on invoices over ~$170. Recommended for B2B.
          </p>
        </div>
        <Switch
          checked={settings.accept_ach}
          onCheckedChange={(v) => update({ accept_ach: v })}
          disabled={saving}
          data-testid="payment-accept-ach-toggle"
        />
      </div>

      {/* Card-fee absorb toggle */}
      <div className="flex items-start justify-between gap-4 py-3 border-t border-zinc-100 dark:border-white/5">
        <div className="flex-1">
          <div className="font-medium text-sm flex items-center gap-2">
            <Percent className="w-4 h-4 text-amber-500" />
            Customer pays the card processing fee
          </div>
          <p className="text-xs text-muted-foreground mt-1 max-w-md">
            When ON, the client sees an inflated total (invoice + 2.9% + $0.30) at checkout so
            you receive the full invoice net. When OFF, you absorb the Stripe fee.
          </p>
        </div>
        <Switch
          checked={settings.absorb_card_fee}
          onCheckedChange={(v) => update({ absorb_card_fee: v })}
          disabled={saving}
          data-testid="payment-absorb-card-fee-toggle"
        />
      </div>

      {/* Platform fee (CountBucks operator revenue) — read-only, set by CountBucks */}
      <div className="py-3 border-t border-zinc-100 dark:border-white/5">
        <div className="font-medium text-sm flex items-center gap-2 mb-1">
          <DollarSign className="w-4 h-4 text-emerald-500" />
          CountBucks platform fee
        </div>
        <p className="text-xs text-muted-foreground max-w-md" data-testid="platform-fee-readonly">
          {settings.platform_fee_mode === 'off'
            ? 'Waived for your account — no platform fee is applied to your invoice payments.'
            : settings.platform_fee_mode === 'flat'
            ? `A flat $${((settings.platform_fee_flat_cents || 0) / 100).toFixed(2)} per payment is applied when clients pay your invoices online.`
            : `${settings.platform_fee_percent ?? 0.6}% per payment${
                settings.platform_fee_cap_cents ? ` (capped at $${(settings.platform_fee_cap_cents / 100).toFixed(0)})` : ''
              } is applied when clients pay your invoices online.`}{' '}
          This rate is set by CountBucks and cannot be changed here.
        </p>
      </div>
    </div>
  );
}
